import type { ImageLoader } from 'next/image';

import MatchedMap from 'utilities/src/matchedMap';

/**
 * UseImageParse Types
 */

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const imageLoaderTypes = ['New Cloudinary', 'Local'] as const;
type ImageLoaderType = (typeof imageLoaderTypes)[number];

export type UseImageParseProps = {
  src: string;
};

type UseImageParseReturn = {
  parsedSrc: string;
  type: ImageLoaderType;
  cloudinaryUrl?: string;
  loader?: ImageLoader;
  unoptimized?: boolean;
};

type ParseImageFn = (props: UseImageParseProps) => UseImageParseReturn;

/**
 * Image Parsers
 */

const parseNewCloudinary: ParseImageFn = ({ src }) => {
  const imgPath = src.split(/(?=www)/)[1];
  const unoptimized = imgPath.endsWith('.svg');
  const parsedSrc = unoptimized ? imgPath : imgPath.replace(/\..+$/, '');

  return {
    parsedSrc,
    type: 'New Cloudinary',
    cloudinaryUrl: process.env.NEXT_PUBLIC_IMAGES_CLOUDINARY_NEW_BASE_URL?.replace('https://', ''),
    unoptimized,
  };
};

const parseLocalImage: ParseImageFn = ({ src }) => ({
  parsedSrc: src,
  type: 'Local',
  loader: src.startsWith(`${process.env.NEXT_PUBLIC_BASE_PATH}/api/`)
    ? ({ src: imgSrc, width }) => `${imgSrc}?w=${width}`
    : undefined,
});

/**
 * Image Loader Map
 */

const imageLoaderMap = new MatchedMap<string, ParseImageFn>([
  ['https://assets', parseNewCloudinary],
  ['_', parseLocalImage],
]);

/**
 * UseImageParse Hook
 */

export const useImageParse = ({ src }: UseImageParseProps) => {
  const imgPathStart = src.split('.')[0];

  const imageLoader = imageLoaderMap.get(imgPathStart);
  const { type, loader, parsedSrc, cloudinaryUrl, unoptimized } = imageLoader({ src });

  return {
    type,
    loader,
    parsedSrc,
    cloudinaryUrl,
    unoptimized,
  };
};

export default useImageParse;
